import { Seat, SeatReference } from '@3ddv/igsu-models/definitions/seat.model';
import { BuyerTypeModel, PriceScaleModel, SectionModel } from '@3ddv/igsu-models/definitions/section.model';
import { SelectedTicketModel } from '@3ddv/igsu-models/definitions/ticket.model';
import { computeTicketPriceWithExchangeType } from '@3ddv/utils-igsu';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ISectionsPriceRange } from '../../../types/sections-price-range';
import { EventsTicketsService } from '../../events/data-access/events-tickets.service';
import { EventsService } from '../../events/data-access/events.service';
import { PriceScalesService } from '../../events/data-access/price-scales.service';
import { SeatsService } from './seats.service';
import { SectionsService } from './sections.service';

export interface ComputedAvailability {
  sectionId: string;
  sectionName: string;
  priceScaleId: string;
  priceScaleName: string;
  buyerTypeId: string;
  buyerTypeName: string;
  price: number;
  oldPrice: number;
  newPrice: number;
  occurrences: number;
  seatId?: number;
  seatNumber?: string;
  row?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ExchangeService {
  private _eventService = inject(EventsService);
  private _sectionsService = inject(SectionsService);
  private _seatsService = inject(SeatsService);
  private _priceScalesService = inject(PriceScalesService);
  private _eventTicketsService = inject(EventsTicketsService);

  private readonly _computedAvailability = new BehaviorSubject<ComputedAvailability[] | null>(null);
  public readonly _computedAvailability$ = this._computedAvailability.asObservable();

  /**
   * Gets the computed availability data.
   * @returns - The computed availability data.
   */
  public getComputedData(): ComputedAvailability[] | null {
    return this._computedAvailability.getValue();
  }

  /**
   * Computes the total price for the best available tickets based on the selected section and price scale.
   *
   * - Retrieves the selected section ID and price scale ID from their respective services.
   * - Filters the computed availability data to match the selected section and price scale.
   * - Calculates the total price by summing the prices of the filtered items, multiplied by their occurrences.
   * - If the section ID or price scale ID is missing, logs a warning and returns 0.
   *
   * @returns The total price of the available tickets for the selected section and price scale, or 0 if any required data is missing.
   */
  public computeBestAvailableTotalPrice() {
    const selectedSectionId = this._sectionsService.getSelectedSectionData()?.sectionID ?? null;
    const selectedPriceScaleId = this._priceScalesService.getSelectedPriceScale() ?? null;
    const computedData = this._computedAvailability.getValue() ?? [];

    if (selectedSectionId === null || selectedPriceScaleId === null) {
      console.warn('Missing selected section ID or price scale ID.');
      return 0;
    }

    const filteredItems = computedData.filter(
      item => +item.sectionId === +selectedSectionId && +item.priceScaleId === +selectedPriceScaleId
    );

    const totalPrice = filteredItems.reduce((sum, item) => sum + item.price * item.occurrences, 0);

    return totalPrice;
  }

  /**
   * Computes the ticket price with exchange type.
   * @param oldTicketPrice - The old ticket price.
   * @param newTicketPrice - The new ticket price.
   * @returns - The computed ticket price with exchange type.
   */
  public computePriceWithExchangeType(oldTicketPrice: number, newTicketPrice: number): number {
    const eventConfiguration = this._eventService.getEventConfiguration()!;
    return computeTicketPriceWithExchangeType(oldTicketPrice, newTicketPrice, eventConfiguration);
  }

  /**
   * Computes availability from section availability data.
   * @param availability - The section availability data.
   * @returns - The computed availability data.
   */
  public getComputedAvailabilityFromSectionAvailability(availability: SectionModel[]): ComputedAvailability[] {
    const eventConfig = this._eventService.getEventConfiguration()!;
    const isBuyerTypePreserved = eventConfig?.shouldPreserveBuyerType;
    const computedAvailability: ComputedAvailability[] = [];

    for (const section of availability) {
      for (const priceScale of section.priceScales) {
        if (isBuyerTypePreserved) {
          this.processSectionWithBuyerTypePreservation(priceScale, section, computedAvailability);
        } else {
          this.processSectionWithoutBuyerTypePreservation(
            priceScale,
            section,
            computedAvailability,
            eventConfig.buyerType
          );
        }
      }
    }

    this._computedAvailability.next(computedAvailability);
    return computedAvailability;
  }

  /**
   * Computes the availability for seats, either preserving or not preserving buyer types.
   *
   * - Iterates through the provided seat availability data and computes availability based on the event configuration.
   * - Calls different processing methods based on whether buyer types should be preserved.
   * - Updates the computed availability observable.
   *
   * @param availability - The list of seat availability data.
   * @param reference - The reference data that includes sections, price scales, and buyer types.
   * @returns The computed availability data.
   */
  public getComputedAvailabilityFromSeatAvailability(availability: Seat[], reference: any): ComputedAvailability[] {
    const eventConfig = this._eventService.getEventConfiguration()!;
    const isBuyerTypePreserved = eventConfig?.shouldPreserveBuyerType;
    const computedAvailability: ComputedAvailability[] = [];

    for (const seat of availability) {
      if (isBuyerTypePreserved) {
        this.processSeatWithBuyerTypePreservation(seat, computedAvailability, reference);
      } else {
        this.processSeatWithoutBuyerTypePreservation(seat, computedAvailability, eventConfig.buyerType, reference);
      }
    }

    this._computedAvailability.next(computedAvailability);
    return computedAvailability;
  }

  /**
   * Processes availability data when buyer types are preserved.
   *
   * - Filters the price scales by available buyer types.
   * - Checks if all unique buyer types from selected tickets are available in the price scale.
   * - Computes availability by comparing the old and new prices for each ticket.
   *
   * @param priceScale - The price scale data.
   * @param section - The section data.
   * @param computedAvailability - The list to store computed availability results.
   */
  private processSectionWithBuyerTypePreservation(
    priceScale: PriceScaleModel,
    section: SectionModel,
    computedAvailability: ComputedAvailability[]
  ) {
    const selectedTickets = this._eventTicketsService.getSelectedTicketsData();
    const uniqueBuyerTypes = this.getUniqueBuyerTypes(selectedTickets);
    const buyerTypeOccurrences = this.getBuyerTypeOccurrences(selectedTickets);
    const availableBuyerTypeIds = this.getAvailableBuyerTypeIds(priceScale);

    const areAllUniqueBuyerTypesAvailable = [...uniqueBuyerTypes].every(uniqueType =>
      availableBuyerTypeIds.includes(uniqueType)
    );

    if (areAllUniqueBuyerTypesAvailable) {
      for (const buyerType of priceScale.buyerTypes) {
        const buyerTypeId = buyerType.buyerType.buyerTypeID.toString();
        const newPrice = buyerType.buyerType.price.value;

        const matchingTicket = selectedTickets!.find(ticket => ticket.buyerType.toString() === buyerTypeId);
        if (matchingTicket) {
          const oldPrice = matchingTicket.price.value;
          const occurrences = buyerTypeOccurrences[buyerTypeId] || 0;

          computedAvailability.push(
            this.buildComputedAvailabilitySectionData(
              section,
              priceScale,
              buyerType.buyerType,
              oldPrice,
              newPrice,
              occurrences
            )
          );
        }
      }
    }
  }

  /**
   * Gets the occurrences of each buyer type from the selected tickets.
   * @param selectedTickets - The selected tickets data.
   * @returns - An object containing the occurrences of each buyer type.
   */
  private getBuyerTypeOccurrences(selectedTickets: SelectedTicketModel): { [key: string]: number } {
    return selectedTickets!.reduce(
      (accumulator, ticket) => {
        const buyerTypeString = ticket.buyerType.toString();
        accumulator[buyerTypeString] = (accumulator[buyerTypeString] || 0) + 1;
        return accumulator;
      },
      {} as { [key: string]: number }
    );
  }

  /**
   * Gets the unique buyer types from the selected tickets.
   * @param selectedTickets - The selected tickets data.
   * @returns - A set of unique buyer types.
   */
  private getUniqueBuyerTypes(selectedTickets: SelectedTicketModel): Set<string> {
    return new Set(selectedTickets?.map(ticket => ticket.buyerType.toString()));
  }

  /**
   * Gets the available buyer type IDs from the price scale.
   * @param priceScale - The price scale data.
   * @returns - An array of available buyer type IDs.
   */
  private getAvailableBuyerTypeIds(priceScale: PriceScaleModel): string[] {
    return priceScale.buyerTypes.map(buyerType => buyerType.buyerType.buyerTypeID.toString());
  }

  /**
   * Processes price scales and sections without buyer type preservation.
   * @param priceScale - The price scale data.
   * @param section - The section data.
   * @param computedAvailability - The computed availability data.
   * @param eventBuyerType - The event buyer type.
   */
  private processSectionWithoutBuyerTypePreservation(
    priceScale: PriceScaleModel,
    section: SectionModel,
    computedAvailability: ComputedAvailability[],
    eventBuyerType: number
  ) {
    const selectedTickets = this._eventTicketsService.getSelectedTicketsData()!;
    const matchingBuyerTypeConfig = priceScale.buyerTypes.find(
      buyerType => +buyerType.buyerType.buyerTypeID === +eventBuyerType
    );

    if (matchingBuyerTypeConfig) {
      for (const ticket of selectedTickets) {
        const oldPrice = ticket.price.value;
        const newPrice = matchingBuyerTypeConfig.buyerType.price.value;

        computedAvailability.push(
          this.buildComputedAvailabilitySectionData(
            section,
            priceScale,
            matchingBuyerTypeConfig.buyerType,
            oldPrice,
            newPrice,
            1
          )
        );
      }
    }
  }

  /**
   * Builds the computed availability data.
   * @param section - The section data.
   * @param priceScale - The price scale data.
   * @param buyerType - The buyer type data.
   * @param oldPrice - The old price.
   * @param newPrice - The new price.
   * @param occurrences - The occurrences of the buyer type.
   * @returns - The computed availability data.
   */
  private buildComputedAvailabilitySectionData(
    section: SectionModel,
    priceScale: PriceScaleModel,
    buyerType: BuyerTypeModel,
    oldPrice: number,
    newPrice: number,
    occurrences: number
  ): ComputedAvailability {
    const eventConfig = this._eventService.getEventConfiguration();
    const field = eventConfig?.sectionDescriptionField;
    const key = field
      ? (`section${field.charAt(0).toUpperCase() + field.slice(1)}` as keyof SectionModel)
      : 'sectionDescription';

    return {
      sectionName: (section[key] as string) || section.sectionDescription,
      sectionId: section.sectionID.toString(),
      priceScaleId: priceScale.priceScaleID,
      priceScaleName: priceScale.derivedDescription,
      buyerTypeId: buyerType.buyerTypeID,
      buyerTypeName: buyerType.buyerType,
      price: this.computePriceWithExchangeType(oldPrice, newPrice),
      oldPrice,
      newPrice,
      occurrences,
    };
  }

  /**
   * Processes the seat data without preserving buyer type and computes availability.
   *
   * - Looks up the section, price scale, and buyer type based on the provided seat and reference data.
   * - Calculates the old and new price for the seat and pushes the computed availability to the list.
   *
   * @param seat - The seat data.
   * @param computedAvailability - The list to store computed availability results.
   * @param eventBuyerType - The buyer type ID from the event.
   * @param reference - The seat reference data.
   */
  private processSeatWithoutBuyerTypePreservation(
    seat: Seat,
    computedAvailability: ComputedAvailability[],
    eventBuyerType: number,
    reference: SeatReference
  ) {
    const sections = reference.sections;
    const priceScales = reference.priceScales;
    const buyerTypes = reference.buyerTypes;

    const selectedSeatData = this._seatsService.getSeatData(seat.id, eventBuyerType);
    if (!selectedSeatData) {
      return;
    }

    const section = sections.find((section: { refKey: string }) => section.refKey === seat.section.refKey);
    const seatPriceScale = priceScales.find(
      (priceScale: { refKey: any }) => priceScale.refKey === seat.priceScale.refKey
    );
    const buyerType = buyerTypes.find((buyerType: { refKey: any }) => +buyerType.refKey === +eventBuyerType);

    if (section == null || seatPriceScale == null || buyerType == null) {
      return;
    }

    const selectedTickets = this._eventTicketsService.getSelectedTicketsData();

    if (selectedTickets) {
      for (const ticket of selectedTickets) {
        const oldPrice = ticket.price.value;
        const newPrice = selectedSeatData.priceData.price.value;

        computedAvailability.push(
          this.buildComputedAvailabilitySeatData(seat, section, seatPriceScale, buyerType, oldPrice, newPrice, 1)
        );
      }
    }
  }

  /**
   * Processes the seat data with buyer type preservation and computes availability.
   *
   * - Looks up the section, price scale, and buyer type based on the provided seat and reference data.
   * - Calculates the old and new price for the seat, preserving buyer types, and pushes the computed availability to the list.
   *
   * @param seat - The seat data.
   * @param computedAvailability - The list to store computed availability results.
   * @param reference - The seat reference data.
   */
  private processSeatWithBuyerTypePreservation(
    seat: Seat,
    computedAvailability: ComputedAvailability[],
    reference: SeatReference
  ) {
    const sections = reference.sections;
    const priceScales = reference.priceScales;
    const buyerTypes = reference.buyerTypes;
    const section = sections.find((section: { refKey: string }) => section.refKey === seat.section.refKey)!;
    const seatPriceScale = priceScales.find(
      (priceScale: { refKey: any }) => priceScale.refKey === seat.priceScale.refKey
    )!;

    const selectedTickets = this._eventTicketsService.getSelectedTicketsData();
    const uniqueBuyerTypes = this.getUniqueBuyerTypes(selectedTickets);
    const seatData = this._seatsService.getSeatData(seat.id, this._eventService.getEventConfiguration()!.buyerType);
    const buyerTypeId = seatData?.priceData.buyerType.refKey!;
    const buyerType = buyerTypes.find((buyerType: { refKey: any }) => +buyerType.refKey === +buyerTypeId)!;

    const isBuyerTypeInSelectedTickets = uniqueBuyerTypes.has(buyerTypeId);

    if (isBuyerTypeInSelectedTickets) {
      const newPrice = seatData?.priceData.price.value;
      const matchingTicket = selectedTickets!.find(ticket => +ticket.buyerType === +buyerTypeId);
      if (matchingTicket) {
        const oldPrice = matchingTicket.price.value;
        computedAvailability.push(
          this.buildComputedAvailabilitySeatData(seat, section, seatPriceScale, buyerType, oldPrice, newPrice!, 1)
        );
      }
    }
  }

  /**
   * Builds computed availability data for seat-based processing.
   *
   * - Constructs an availability object with section, price scale, buyer type, and seat-specific information.
   *
   * @param seat - The seat data.
   * @param section - The section data.
   * @param priceScale - The price scale data.
   * @param buyerType - The buyer type data.
   * @param oldPrice - The old price of the ticket.
   * @param newPrice - The new price of the ticket.
   * @param occurrences - The number of occurrences for this buyer type.
   * @returns The computed availability data.
   */
  private buildComputedAvailabilitySeatData(
    seat: Seat,
    section: SeatReference['sections'][number],
    priceScale: SeatReference['priceScales'][number],
    buyerType: SeatReference['buyerTypes'][number],
    oldPrice: number,
    newPrice: number,
    occurrences: number
  ): ComputedAvailability {
    const eventConfig = this._eventService.getEventConfiguration();
    const field = eventConfig?.sectionDescriptionField;

    // SeatReference.Section uses 'description' and 'publicDescription' (no "section" prefix),
    // while the config stores values like 'description', 'publicDescription', 'shortPublicDescription'.
    const key = (field ?? 'publicDescription') as keyof SeatReference['sections'][number];

    return {
      sectionName: (section[key] as string) || section.publicDescription,
      sectionId: section.id.toString(),
      priceScaleId: priceScale.id.toString(),
      priceScaleName: priceScale.publicDescription,
      buyerTypeId: buyerType.id.toString(),
      buyerTypeName: buyerType.publicDescription,
      price: this.computePriceWithExchangeType(oldPrice, newPrice),
      oldPrice,
      newPrice,
      occurrences,
      seatId: seat.id,
      seatNumber: seat.seatNumber,
    };
  }

  /**
   * Calculates the price ranges for each section from the computed availability data.
   *
   * - Iterates over the computed availability data to determine the lowest and highest prices for each section.
   *
   * @param data - The computed availability data.
   * @returns An array of price range objects for each section.
   */
  public calculateSectionPriceRanges(data: ComputedAvailability[] | null): ISectionsPriceRange[] {
    if (data == null) {
      return [];
    }

    const sectionPriceRanges = data.reduce((accumulator, item) => {
      if (accumulator[item.sectionId]) {
        accumulator[item.sectionId].lowest = Math.min(accumulator[item.sectionId].lowest, item.price);
        accumulator[item.sectionId].highest = Math.max(accumulator[item.sectionId].highest, item.price);
      } else {
        accumulator[item.sectionId] = {
          sectionName: item.sectionName,
          lowest: item.price,
          highest: item.price,
        };
      }
      return accumulator;
    }, {} as any);

    return Object.keys(sectionPriceRanges).map(sectionId => ({
      sectionId,
      sectionName: sectionPriceRanges[sectionId].sectionName,
      lowest: sectionPriceRanges[sectionId].lowest,
      highest: sectionPriceRanges[sectionId].highest,
      image: null,
    }));
  }
}
